/**
 * Group Memory — persist frequent split groups for fast re-use
 * Stored in localStorage via Zustand persist
 */

export const MAX_GROUPS = 10;
export const MAX_MEMBERS = 20;

/**
 * Extract a "group fingerprint" from a participant list
 * so we can detect when the same people split together again
 */
export const groupFingerprint = (participants) =>
  [...participants]
    .map((p) => p.user_id || p.display_name)
    .sort()
    .join('|');

/**
 * Score how closely a saved group matches current participants
 * Returns 0–1 (1 = perfect match)
 */
export const matchScore = (savedGroup, currentParticipants) => {
  const savedIds = new Set(savedGroup.members.map((m) => m.user_id || m.display_name));
  const currentIds = new Set(currentParticipants.map((p) => p.user_id || p.display_name));
  const intersection = [...savedIds].filter((id) => currentIds.has(id)).length;
  const union = new Set([...savedIds, ...currentIds]).size;
  return union > 0 ? intersection / union : 0;
};

/**
 * Find suggested groups for the current participants (score > 0.5)
 */
export const suggestGroups = (savedGroups, currentParticipants) =>
  savedGroups
    .map((g) => ({ ...g, score: matchScore(g, currentParticipants) }))
    .filter((g) => g.score > 0.4)
    .sort((a, b) => b.score - a.score);

/**
 * Build a group record from a completed session
 */
export const buildGroupFromSession = (session, participants) => ({
  id: `group-${Date.now()}`,
  name: session.name,
  members: participants.map((p) => ({
    user_id: p.user_id,
    display_name: p.display_name,
    avatar_color: p.avatar_color,
  })),
  last_used: new Date().toISOString(),
  times_used: 1,
  total_split: 0,
});
